/**
 * Trust Centre — public page at /trust
 *
 * Iteration 119. Unauthenticated. Renders ONLY controls the backend
 * verifiably evaluated; nothing marketing, nothing exaggerated.
 *
 * Contract: GET /api/trust/public returns a stable shape (see
 * routes/trust_public.py). This component is a thin renderer over
 * that response — it does not invent or upscore anything.
 */
import { useEffect, useState } from "react";
import LegalFooterInfo from "@/components/LegalFooterInfo";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck, Lock, Database, FileText, AlertTriangle, CheckCircle2,
  Globe, ScrollText, Mail, Clock,
} from "lucide-react";

const STATUS_TONE = {
  pass: { dot: "bg-emerald-500", text: "text-emerald-800", chip: "bg-emerald-100" },
  warn: { dot: "bg-amber-500", text: "text-amber-900", chip: "bg-amber-100" },
  fail: { dot: "bg-rose-500", text: "text-rose-900", chip: "bg-rose-100" },
  manual: { dot: "bg-slate-400", text: "text-slate-700", chip: "bg-slate-100" },
};

function StatusBadge({ status }) {
  const t = STATUS_TONE[status] || STATUS_TONE.manual;
  return (
    <Badge className={`${t.chip} ${t.text} border-0 rounded-sm text-[10px] uppercase tracking-wider px-1.5 py-0.5`}>
      {status}
    </Badge>
  );
}

function ControlBlock({ row }) {
  return (
    <div className="py-3 border-b border-slate-100 last:border-b-0" data-testid={`trust-public-ctrl-${row.id}`}>
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-sm font-medium text-slate-900">{row.title}</span>
        <StatusBadge status={row.status} />
        {(row.standards || []).slice(0, 2).map((s) => (
          <span key={s} className="text-[10px] text-slate-500 font-mono-data">{s}</span>
        ))}
      </div>
      <p className="text-xs text-slate-600 mt-1">{row.evidence}</p>
    </div>
  );
}

function CertCard({ name, cert, testId }) {
  const status = (cert?.status || "in_progress").replace("_", " ");
  const isCertified = cert?.status === "certified";
  return (
    <Card className="p-5" data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">{name}</div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-0.5">{status}</div>
        </div>
        {isCertified ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
        ) : (
          <Clock className="w-5 h-5 text-amber-600" />
        )}
      </div>
      <p className="text-xs text-slate-600 mt-3">{cert?.details}</p>
      {cert?.certified_until && (
        <p className="text-[11px] text-slate-500 mt-2 font-mono-data">
          Certified until {cert.certified_until}
        </p>
      )}
      {cert?.auditor && (
        <p className="text-[11px] text-slate-500 mt-1 font-mono-data">
          Auditor: {cert.auditor}
        </p>
      )}
    </Card>
  );
}

export default function TrustCentre() {
  const [data, setData] = useState(null);
  const [incidents, setIncidents] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/trust/public")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e.message));
    api.get("/trust/incident-history")
      .then((r) => setIncidents(r.data))
      .catch(() => { /* non-fatal */ });
  }, []);

  if (error) {
    return (
      <main className="min-h-screen bg-slate-50 p-8">
        <Card className="max-w-2xl mx-auto p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <div className="font-semibold text-slate-900 mb-1">Trust Centre unavailable</div>
              <div className="text-sm text-slate-700">{error}</div>
            </div>
          </div>
        </Card>
      </main>
    );
  }
  if (!data) return <main className="p-8 text-sm text-slate-600">Loading Trust Centre…</main>;

  return (
    <main className="min-h-screen bg-slate-50" data-testid="trust-centre-public">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div className="flex items-center gap-3 mb-3">
            <ShieldCheck className="w-7 h-7 text-teal-700" />
            <h1 className="text-3xl font-semibold text-slate-900">Trust Centre</h1>
          </div>
          <p className="text-slate-700 max-w-2xl">
            Every figure on this page is computed from live system state and verifiable controls &mdash; never marketing copy.
            CoParent Console is built to preserve secure, court-ready records of every parenting event &mdash; messages, expenses,
            handovers, supervised contact and parenting time &mdash; with tamper-evident audit trails, AES-GCM field-level
            encryption and ISO 27001-aligned operating controls. This is the same controls-backed view our compliance team
            uses internally.
          </p>
          <p className="text-[11px] text-slate-500 mt-3 font-mono-data">
            Generated {data.generated_at?.slice(0, 19)} · Operator: {data.platform?.operator} · Region: {data.platform?.region}
          </p>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Section: System Status */}
        <section className="mb-8" data-testid="trust-section-system-status">
          <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Globe className="w-5 h-5 text-teal-700" /> System status
          </h2>
          <Card className="p-5">
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              {Object.entries(data.system_status || {}).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full ${v === "ok" ? "bg-emerald-500" : "bg-amber-500"}`} />
                  <span className="capitalize text-slate-700">{k.replace(/_/g, " ")}</span>
                  <span className={`ml-auto font-mono-data text-xs ${v === "ok" ? "text-emerald-700" : "text-amber-700"}`}>{v}</span>
                </div>
              ))}
            </dl>
          </Card>
        </section>

        {/* Section: Security Status */}
        <section className="mb-8" data-testid="trust-section-security">
          <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-teal-700" /> Current security status
          </h2>
          <Card className="p-5">
            {(data.security || []).map((row) => <ControlBlock row={row} key={row.id} />)}
          </Card>
        </section>

        {/* Section: Data Protection */}
        <section className="mb-8" data-testid="trust-section-data-protection">
          <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Lock className="w-5 h-5 text-teal-700" /> Data protection &amp; resilience
          </h2>
          <Card className="p-5">
            {(data.data_protection || []).map((row) => <ControlBlock row={row} key={row.id} />)}
          </Card>
        </section>

        {/* Section: Compliance */}
        <section className="mb-8" data-testid="trust-section-compliance">
          <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-teal-700" /> Compliance &amp; certifications
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <CertCard name="ISO 27001" cert={data.compliance?.iso27001} testId="trust-cert-iso27001" />
            <CertCard name="SOC 2 Type II" cert={data.compliance?.soc2} testId="trust-cert-soc2" />
            <CertCard name="UK / EU GDPR" cert={data.compliance?.gdpr} testId="trust-cert-gdpr" />
          </div>
          {!data.compliance?.iso27001?.status?.includes("certified") &&
            !data.compliance?.soc2?.status?.includes("certified") && (
              <p className="text-xs text-slate-500 mt-3">
                We deliberately don&apos;t display certification badges we have not earned. ISO 27001 and SOC 2 readiness work is in progress; certifications will appear here automatically the day they are granted.
              </p>
            )}
        </section>

        {/* Section: Malware protection (engine name) */}
        {data.av_engine && (
          <section className="mb-8" data-testid="trust-section-av">
            <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Database className="w-5 h-5 text-teal-700" /> Malware protection
            </h2>
            <Card className="p-5">
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`inline-block w-2 h-2 rounded-full ${data.av_engine.is_production_engine ? "bg-emerald-500" : "bg-amber-500"}`} />
                <span className="text-sm text-slate-800">
                  Active scanning engine:{" "}
                  <span className="font-mono-data" data-testid="trust-av-engine-name">
                    {data.av_engine.engine}
                  </span>
                  {data.av_engine.engine_version && (
                    <span className="ml-2 text-slate-500 text-xs">
                      v{data.av_engine.engine_version}
                    </span>
                  )}
                </span>
                {data.av_engine.is_production_engine ? (
                  <Badge className="bg-emerald-100 text-emerald-900 border-0 rounded-sm">Production engine</Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-900 border-0 rounded-sm">Non-production engine</Badge>
                )}
              </div>
              <p className="text-xs text-slate-600 mt-3">
                Every uploaded file is scanned before release. Failed or infected uploads are automatically quarantined and never made available for download.
              </p>
            </Card>
          </section>
        )}

        {/* Section: Policies */}
        <section className="mb-8" data-testid="trust-section-policies">
          <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <ScrollText className="w-5 h-5 text-teal-700" /> Policies
          </h2>
          <Card className="p-5">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(data.policies || []).map((p) => (
                <li key={p.url} className="text-sm">
                  <Link to={p.url} className="text-teal-700 hover:underline">{p.title} →</Link>
                </li>
              ))}
            </ul>
          </Card>
        </section>

        {/* Section: Incident history */}
        {incidents && (
          <section className="mb-8" data-testid="trust-section-incidents">
            <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-700" /> Incident history
            </h2>
            <Card className="p-5">
              {incidents.count === 0 ? (
                <p className="text-sm text-slate-600">
                  No public incident summaries have been published. {incidents.policy}
                </p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {incidents.incidents.map((i, ix) => (
                    <li key={ix} className="py-3">
                      <div className="flex items-baseline gap-2">
                        <span className="font-mono-data text-xs text-slate-700">{i.opened_at}</span>
                        <Badge className={`${STATUS_TONE.warn.chip} ${STATUS_TONE.warn.text} border-0 rounded-sm text-[10px] uppercase`}>{i.severity}</Badge>
                        <span className="text-[10px] uppercase text-slate-500 tracking-wider">{i.category}</span>
                      </div>
                      <p className="text-sm text-slate-700 mt-1">{i.resolution_summary}</p>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-[11px] text-slate-500 mt-3">{incidents.policy}</p>
            </Card>
          </section>
        )}

        {/* Section: Responsible disclosure */}
        <section className="mb-8" data-testid="trust-section-disclosure">
          <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Mail className="w-5 h-5 text-teal-700" /> Responsible disclosure
          </h2>
          <Card className="p-5">
            <p className="text-sm text-slate-700 mb-3">
              Found a security issue? We&apos;d genuinely like to hear about it. Please send a private report to:
            </p>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-slate-500">Email: </span>
                <a className="text-teal-700 hover:underline font-mono-data" href={`mailto:${data.responsible_disclosure?.email}`}>
                  {data.responsible_disclosure?.email}
                </a>
              </div>
              <div>
                <span className="text-slate-500">Policy: </span>
                <Link to={data.responsible_disclosure?.policy_url || "/security"} className="text-teal-700 hover:underline">{data.responsible_disclosure?.policy_url || "/security"}</Link>
              </div>
              <div>
                <span className="text-slate-500">security.txt: </span>
                <a className="text-teal-700 hover:underline font-mono-data" href={data.responsible_disclosure?.security_txt || "/.well-known/security.txt"}>
                  {data.responsible_disclosure?.security_txt || "/.well-known/security.txt"}
                </a>
              </div>
            </div>
          </Card>
        </section>

        <footer className="text-[11px] text-slate-500 mt-12">
          This page is generated dynamically. For institutional buyers and procurement teams, our{" "}
          <Link to="/trust/due-diligence" className="text-teal-700 hover:underline" data-testid="trust-cta-due-diligence">
            Procurement &amp; Due Diligence Centre
          </Link>{" "}
          contains the full evidence-backed view including DPA, encryption standards, BCP/DR, pentest history and certification roadmap.
          <LegalFooterInfo />
        </footer>
      </div>
    </main>
  );
}
